import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Plan } from "@edu/shared";
import * as api from "../../../lib/api.ts";
import { answerableOf, type AnswerableView } from "./answerable.ts";

/**
 * Состояние ветки «Из заготовки»: выбранная заготовка и снятые флажки (5.5
 * docs/homework-phase2-design.md).
 *
 * Отдельным модулем от формы затем же, зачем ветка «По темам»: у формы своих
 * вопросов хватает, а обе ветки обязаны выглядеть в ней одинаково, то есть
 * состоянием, которое она только читает.
 */

export interface PlanSourceState {
  planId: string;
  plan: Plan | null;
  answerable: AnswerableView[];
  excluded: ReadonlySet<string>;
  /**
   * Задания, которые уедут в домашку: отмеченные И с верным ответом.
   *
   * ⚠️ **Задание без верного ответа сюда не попадает, даже отмеченное**, и это
   * не мелочь оформления. Сервер такое задание молча не берёт
   * (`apps/api/src/homework-exercises.ts`), поэтому список, включающий его,
   * означал бы предпросмотр «Задания (8)» над пятью созданными работами и число
   * заданий, которое врёт. Отказом сервер отвечать не может: у пропусков и
   * свободного ввода эталона нет, пока человек печатает условие.
   *
   * ⚠️ Из `excluded` такое задание при этом НЕ выбрасывается: снятый флажок это
   * «сделаем на уроке», решение человека, а «пока без верного ответа» это
   * состояние задания. Смешав их, мы потеряли бы выбор человека в тот момент,
   * когда он доделает задание в редакторе.
   */
  picked: AnswerableView[];
  err: string | null;
  setPlanId: (id: string) => void;
  toggle: (elementId: string) => void;
  /** Восстановление брошенной раздачи: заготовка и её состав. */
  restore: (planId: string, elementIds: readonly string[] | null) => void;
}

export function usePlanSource(initialPlanId: string): PlanSourceState {
  const [planId, setPlanId] = useState<string>(initialPlanId);
  const [plan, setPlan] = useState<Plan | null>(null);
  /** Снятый флажок это «сделаем на уроке» или «без слайда непонятно» (3.1 проекта). */
  const [excluded, setExcluded] = useState<ReadonlySet<string>>(new Set());
  const [err, setErr] = useState<string | null>(null);
  /** Восстановленный из плана состав: применяется, когда заготовка догрузится. */
  const restoredElementIds = useRef<string[] | null>(null);

  const loadPlan = useCallback(async (id: string) => {
    setPlan(null);
    setExcluded(new Set());
    if (id === "") return;
    try {
      setPlan(await api.getPlan(id));
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void loadPlan(planId);
  }, [planId, loadPlan]);

  const answerable = useMemo(() => answerableOf(plan), [plan]);

  // Состав восстановленной раздачи ждёт заготовку: до её загрузки применять его
  // не к чему
  useEffect(() => {
    const wanted = restoredElementIds.current;
    if (wanted === null || answerable.length === 0) return;
    restoredElementIds.current = null;
    const keep = new Set(wanted);
    setExcluded(new Set(answerable.filter((ex) => !keep.has(ex.id)).map((ex) => ex.id)));
  }, [answerable]);

  return {
    planId,
    plan,
    answerable,
    excluded,
    picked: answerable.filter((ex) => ex.ready && !excluded.has(ex.id)),
    err,
    setPlanId,
    toggle: (elementId) =>
      setExcluded((prev) => {
        const next = new Set(prev);
        if (next.has(elementId)) next.delete(elementId);
        else next.add(elementId);
        return next;
      }),
    restore: (id, elementIds) => {
      restoredElementIds.current = elementIds === null ? null : [...elementIds];
      setPlanId(id);
    },
  };
}
